/*
 * Шаги курса «Фортепиано с нуля и нотная грамота».
 *
 * Играть можно четырьмя путями, и каждый — полноправный: экранная клавиатура
 * (палец, мышь), клавиатура компьютера (TimbricaKeymap), MIDI-клавиатура
 * (MidiInput — один владелец Web MIDI на сайте) и настоящее пианино через
 * микрофон (MusicCore: высота тона, хрома, удары). Все четыре сходятся в одну
 * «шину» шага: событие «нажата/отпущена нота» с временем на часах страницы.
 *
 *   piano-find   — найти клавишу (или все клавиши ноты) на клавиатуре;
 *   staff-read   — нота на нотном стане → сыграть её (бесконечный генератор);
 *   piano-play   — мелодия или пьеса по нотам: «с ожиданием» (курс ждёт
 *                  верную ноту) или «в темпе» (метроном, падающие ноты,
 *                  вердикт по каждой ноте: вовремя / неточно / мимо);
 *   rhythm-tap   — простучать ритм под метроном;
 *   piano-chord  — взять аккорд (несколько нот сразу; мышью — «залипанием»).
 * Рисунки: `staff` (ноты через abcjs) и `keys` (клавиатура с отметками).
 *
 * Ноты рисует abcjs (MIT, тот же, что у /audio-to-notes) — грузится только
 * когда в уроке впервые появился нотный стан. Почему не свой SVG и не
 * alphaTab — docs/why/piano-course.md. Числа (микрофон, время) — там же.
 */
(function () {
    'use strict';
    var K = window.CourseKit, MC = window.MusicCore;
    if (!K) return;
    if (MC) MC.install(K);
    var CFG = window.__PIANO_COURSE || {};
    var RAW = (window.__COURSE && window.__COURSE.TT) || {};

    function T(step) { return step.t || {}; }
    function perfNow() { return (window.performance && performance.now ? performance.now() : Date.now()) / 1000; }
    function ae(name, data) { try { if (window.__ae) window.__ae(name, Object.assign({ course: 'piano-course' }, data || {})); } catch (_) {} }

    /* ── ноты и их имена ────────────────────────────────────────────── */

    var LET = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
    var LET_PC = [0, 2, 4, 5, 7, 9, 11];
    var SHARP_SPELL = [[0, 0], [0, 1], [1, 0], [1, 1], [2, 0], [3, 0], [3, 1], [4, 0], [4, 1], [5, 0], [5, 1], [6, 0]];
    var FLAT_SPELL = [[0, 0], [1, -1], [1, 0], [2, -1], [2, 0], [3, 0], [4, -1], [4, 0], [5, -1], [5, 0], [6, -1], [6, 0]];

    /* «F#4», «Bb3», «C4» → {letter 0..6, acc −1|0|1, oct, midi}. */
    function parse(s) {
        if (typeof s === 'number') return fromMidi(s);
        var m = /^([A-Ga-g])(#|b)?(-?\d)$/.exec(String(s).trim());
        if (!m) return null;
        var letter = 'CDEFGAB'.indexOf(m[1].toUpperCase());
        var acc = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
        var oct = parseInt(m[3], 10);
        return { letter: letter, acc: acc, oct: oct, midi: (oct + 1) * 12 + LET_PC[letter] + acc };
    }
    function fromMidi(m, flats) {
        var pc = ((m % 12) + 12) % 12, sp = (flats ? FLAT_SPELL : SHARP_SPELL)[pc];
        return { letter: sp[0], acc: sp[1], oct: Math.floor(m / 12) - 1, midi: m };
    }
    function midiOf(x) { var p = parse(x); return p ? p.midi : null; }
    function pcOf(m) { return ((m % 12) + 12) % 12; }
    function isBlack(m) { var pc = pcOf(m); return pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10; }
    function names() { var n = RAW.names; return Array.isArray(n) && n.length === 7 ? n : LET; }
    /* Имя ноты на языке страницы: До/Do/C/도 + ♯/♭; немецкое H и B — из
       перевода (ui.flat_b). Октава в имя не входит: её показывает клавиатура. */
    function label(p) {
        if (typeof p === 'number') p = fromMidi(p);
        if (!p) return '';
        if (p.letter === 6 && p.acc === -1 && RAW.flat_b) return String(RAW.flat_b);
        return names()[p.letter] + (p.acc === 1 ? '♯' : p.acc === -1 ? '♭' : '');
    }
    /* Имя нажатой клавиши так, как его записала цель (Си♭, а не Ля♯). */
    function labelLike(m, like) {
        if (like && like.midi === m) return label(like);
        return label(fromMidi(m, like && like.acc === -1));
    }

    /* ── звук: синтез рояля ─────────────────────────────────────────── */

    var synthInst = null, synthCtx = null;
    function synthFor(ctx) {
        if (!window.TimbricaPianoSynth) return null;
        if (synthCtx !== ctx || !synthInst) {
            try {
                synthInst = window.TimbricaPianoSynth.create({ ctx: ctx, voice: 'grand', reverb: 'warm' });
                synthInst.init();
                synthCtx = ctx;
            } catch (e) { synthInst = null; }
        }
        return synthInst;
    }
    function wait(ms) { return new Promise(function (r) { setTimeout(r, Math.max(0, ms)); }); }
    /* Сыграть события {t, d (доли), n: [midi]} в темпе bpm с момента at. */
    function schedule(ctx, events, bpm, at, gain) {
        var s = synthFor(ctx);
        if (!s) return 0;
        var spb = 60 / bpm, end = at;
        events.forEach(function (e) {
            var t0 = at + e.t * spb, t1 = t0 + Math.max(0.12, e.d * spb * 0.94);
            (e.n || []).forEach(function (m) { s.noteOn(m, gain || 0.7, t0); s.noteOff(m, t1); });
            end = Math.max(end, t1);
        });
        return end;
    }
    /* Общий звук уроков (choice с audio, «Прослушать») — роялем, не щипком гитары. */
    K.audio.notes = function (notes, o) {
        o = o || {};
        return K.util.audioCtx().then(function (ctx) {
            var list = (Array.isArray(notes) ? notes : [notes]).map(midiOf).filter(function (x) { return x != null; });
            if (!synthFor(ctx)) return MC ? MC.play(ctx, notes, o) : null;
            var at = ctx.currentTime + 0.06, end;
            if (o.mode === 'chord') {
                var dur = o.dur || 1.8;
                end = schedule(ctx, list.map(function (m, i) { return { t: i * (o.strum || 0), d: dur, n: [m] }; }), 60, at);
            } else {
                end = schedule(ctx, list.map(function (m, i) { return { t: i, d: 0.95, n: [m] }; }), o.bpm || 92, at);
            }
            return wait((end - ctx.currentTime + 0.4) * 1000);
        });
    };

    /* ── шина ввода шага ────────────────────────────────────────────── */

    /* Одна шина на шаг: on/off нот от всех источников. Звук страницы — на
       экран, клавиатуру компьютера и MIDI (если не выключен); пианино в
       комнате звучит само. */
    var midiHubs = [];
    function Hub(api) {
        var subs = [], held = {}, used = {};
        var hub = {
            sound: true,
            on: function (m, src, t, vel) {
                if (held[m] && src !== 'mic') return;
                held[m] = src;
                if (!used[src]) { used[src] = 1; ae('course_piano_input', { src: src }); }
                if (src !== 'mic' && (src !== 'midi' || hub.sound)) {
                    api.audioCtx().then(function (ctx) { var s = synthFor(ctx); if (s && held[m]) s.noteOn(m, vel || 0.72); }).catch(function () {});
                }
                subs.forEach(function (f) { try { f('on', m, src, t == null ? perfNow() : t); } catch (e) { if (window.console) console.error(e); } });
            },
            off: function (m, src, t) {
                if (!held[m]) return;
                delete held[m];
                if (synthInst) try { synthInst.noteOff(m); } catch (_) {}
                subs.forEach(function (f) { try { f('off', m, src, t == null ? perfNow() : t); } catch (e) {} });
            },
            listen: function (fn) { subs.push(fn); },
            held: function () { return Object.keys(held).map(Number); },
            releaseAll: function () { Object.keys(held).forEach(function (k) { hub.off(+k, held[k]); }); },
            used: function () { return Object.keys(used); }
        };
        midiHubs.push(hub);
        api.onCleanup(function () {
            hub.releaseAll();
            midiHubs = midiHubs.filter(function (h) { return h !== hub; });
        });
        return hub;
    }

    /* ── MIDI: один сеанс на страницу (MidiInput) ───────────────────── */

    var midiSess = null, midiState = null, midiBars = [];
    function midiSession() {
        if (midiSess || !window.MidiInput) return midiSess;
        midiSess = window.MidiInput.session({
            slug: 'piano-course',
            device: RAW.midi_device || '',
            onMessage: function (m) { midiIn(m); },
            onState: function (st, change) {
                midiState = st;
                midiBars.forEach(function (fn) { try { fn(); } catch (_) {} });
                try { window.MidiInput.toastChange(change); } catch (_) {}
            }
        });
        return midiSess;
    }
    function midiOn() { return !!(midiState && midiState.status === 'on'); }

    /* ── клавиатура компьютера ──────────────────────────────────────── */

    /* lo..hi — что нужно уроку: «daw» (ряд asdf = белые клавиши, как на
       пианино), если хватает 17 полутонов, иначе «sequential» (белые клавиши
       подряд по рядам, Shift — чёрная выше). */
    function bindComputerKeys(api, hub, lo, hi, whites) {
        if (!window.TimbricaKeymap) return null;
        var span = hi - lo;
        var km;
        if (span <= 16) {
            var base = lo - pcOf(lo);                       // До не выше нижней ноты
            if (hi - base > 16) base = lo;
            km = window.TimbricaKeymap.create({ scheme: 'daw', base: base, baseMin: 21, baseMax: 96 });
        } else {
            km = window.TimbricaKeymap.create({ scheme: 'sequential' });
            km.setWhiteKeys(whites);
        }
        var down = {};
        function kd(e) {
            if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
            if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
            var m = km.noteFor(e.code, e.shiftKey);
            if (m == null || m < lo - 12 || m > hi + 12) return;
            e.preventDefault();
            down[e.code] = m;
            hub.on(m, 'keys', perfNow());
        }
        function ku(e) {
            var m = down[e.code];
            if (m == null) return;
            delete down[e.code];
            hub.off(m, 'keys', perfNow());
        }
        document.addEventListener('keydown', kd);
        document.addEventListener('keyup', ku);
        api.onCleanup(function () {
            document.removeEventListener('keydown', kd);
            document.removeEventListener('keyup', ku);
        });
        return km;
    }
    function coarse() { try { return window.matchMedia('(pointer: coarse)').matches && !window.matchMedia('(any-pointer: fine)').matches; } catch (_) { return false; } }

    /* ── экранная клавиатура ────────────────────────────────────────── */

    /*
     * Keyboard(api, hub, {lo, hi, labels: 'all'|'c'|'none'|'marked', marks})
     * → {el, mark(midi, cls), unmark(cls), flash(midi, cls), x(midi)}.
     * Нажатие — pointerdown (у инструмента звук на нажатии, а не на отпускании
     * пальца); мультитач — каждый палец своей клавишей. latch — режим аккорда
     * для мыши: щелчок «залипает» клавишу до второго щелчка.
     */
    function Keyboard(api, hub, o) {
        var lo = o.lo, hi = o.hi;
        while (isBlack(lo)) lo--;
        while (isBlack(hi)) hi++;
        var whites = [];
        for (var m = lo; m <= hi; m++) if (!isBlack(m)) whites.push(m);
        var root = api.el('div', 'pn-kb');
        var wrap = api.el('div', 'pn-kb-wrap');
        wrap.style.setProperty('--n', whites.length);
        var lane = null;
        if (o.lane) {
            lane = api.el('canvas', 'pn-lane');
            lane.setAttribute('aria-hidden', 'true');
            wrap.appendChild(lane);
        }
        var keysBox = api.el('div', 'pn-keys');
        wrap.appendChild(keysBox);
        root.appendChild(wrap);
        var keyEl = {};
        var W = whites.length;
        function addKey(midi, black, left) {
            var k = api.el('button', 'pn-key no-tap-floor ' + (black ? 'pn-b' : 'pn-w'));
            k.type = 'button';
            k.tabIndex = -1;
            k.setAttribute('data-m', midi);
            k.setAttribute('aria-label', label(midi) + ' ' + (Math.floor(midi / 12) - 1));
            if (black) k.style.left = left + '%';
            var lab = api.el('span', 'pn-key-l');
            k.appendChild(lab);
            var hint = api.el('span', 'pn-key-h');
            k.appendChild(hint);
            keysBox.appendChild(k);
            keyEl[midi] = k;
        }
        whites.forEach(function (wm) { addKey(wm, false); });
        whites.forEach(function (wm, i) {
            if (wm + 1 <= hi && isBlack(wm + 1)) addKey(wm + 1, true, (i + 1) * 100 / W);
        });
        function relabel(km) {
            Object.keys(keyEl).forEach(function (k) {
                var mm = +k, el = keyEl[k];
                var show = o.labels === 'all' || (o.labels === 'c' && pcOf(mm) === 0) || (o.labels === 'white' && !isBlack(mm))
                    || (o.labels === 'marked' && el.classList.contains('is-target'));
                el.querySelector('.pn-key-l').textContent = show ? label(mm) : '';
                el.querySelector('.pn-key-h').textContent = km && root.classList.contains('show-letters') ? km.hintFor(mm) : '';
            });
        }
        var latched = {};
        var pointers = {};
        keysBox.addEventListener('pointerdown', function (e) {
            var k = e.target && e.target.closest && e.target.closest('.pn-key');
            if (!k) return;
            e.preventDefault();
            var mm = +k.getAttribute('data-m');
            if (o.latch && e.pointerType === 'mouse') {
                if (latched[mm]) { delete latched[mm]; k.classList.remove('is-down'); hub.off(mm, 'screen'); }
                else { latched[mm] = 1; k.classList.add('is-down'); hub.on(mm, 'screen'); }
                return;
            }
            pointers[e.pointerId] = mm;
            try { k.setPointerCapture(e.pointerId); } catch (_) {}
            hub.on(mm, 'screen');
        });
        function up(e) {
            var mm = pointers[e.pointerId];
            if (mm == null) return;
            delete pointers[e.pointerId];
            hub.off(mm, 'screen');
        }
        keysBox.addEventListener('pointerup', up);
        keysBox.addEventListener('pointercancel', up);
        keysBox.addEventListener('lostpointercapture', up);
        keysBox.addEventListener('contextmenu', function (e) { e.preventDefault(); });
        hub.listen(function (type, mm) {
            var el = keyEl[mm];
            if (!el) return;
            if (type === 'on') el.classList.add('is-down');
            else if (!latched[mm]) el.classList.remove('is-down');
        });
        var K2 = {
            el: root, lo: lo, hi: hi, whites: whites, lane: lane,
            has: function (mm) { return !!keyEl[mm]; },
            mark: function (mm, cls) { if (keyEl[mm]) keyEl[mm].classList.add(cls); },
            unmark: function (cls) { Object.keys(keyEl).forEach(function (k) { keyEl[k].classList.remove(cls); }); },
            flash: function (mm, cls, ms) {
                var el = keyEl[mm];
                if (!el) return;
                el.classList.add(cls);
                setTimeout(function () { el.classList.remove(cls); }, ms || 650);
            },
            unlatch: function () { Object.keys(latched).forEach(function (k) { delete latched[k]; hub.off(+k, 'screen'); if (keyEl[k]) keyEl[k].classList.remove('is-down'); }); },
            latched: function () { return Object.keys(latched).map(Number); },
            relabel: relabel,
            /* x-центр клавиши (0..1) — для дорожки падающих нот */
            x: function (mm) {
                if (!isBlack(mm)) { var i = whites.indexOf(mm); return i < 0 ? -1 : (i + 0.5) / W; }
                var j = whites.indexOf(mm - 1);
                return j < 0 ? -1 : (j + 1) / W;
            },
            keyW: function () { return 1 / W; }
        };
        return K2;
    }

    /* ── строка «Чем играть»: MIDI, микрофон, буквы ─────────────────── */

    /*
     * inputBar(host, api, hub, kb, km, {mic: 'notes'|'chord'|'onsets'|false})
     * Микрофон шаг включает только по нажатию: разрешение и звук — жестом.
     */
    function inputBar(host, api, hub, kb, km, o) {
        o = o || {};
        var bar = api.el('div', 'pn-inputs');
        bar.appendChild(api.el('span', 'pn-inputs-k', api.tt('p_inputs')));
        var letters = null;
        if (km && !coarse()) {
            letters = api.el('button', 'pn-chip');
            letters.type = 'button';
            var on = api.store.get('pn_letters', true);
            function setL(v) {
                kb.el.classList.toggle('show-letters', v);
                letters.setAttribute('aria-pressed', v ? 'true' : 'false');
                letters.textContent = '⌨ ' + api.tt('p_letters');
                kb.relabel(km);
            }
            letters.addEventListener('click', function () { on = !on; api.store.set('pn_letters', on); setL(on); });
            setL(on);
            bar.appendChild(letters);
        } else {
            kb.relabel(km);
        }
        var midiBtn = null;
        if (window.MidiInput) {
            midiSession();
            midiBtn = api.el('button', 'pn-chip');
            midiBtn.type = 'button';
            var refresh = function () {
                if (!api.alive()) return;
                var on2 = midiOn();
                midiBtn.classList.toggle('is-on', on2);
                midiBtn.textContent = '🎹 ' + (on2 ? api.tt('p_midi_on', { names: midiState.names || '' }) : (window.MidiInput.label(midiState) || api.tt('p_midi')));
                soundBtn.hidden = !on2;
            };
            midiBtn.addEventListener('click', function () {
                if (!midiSess) return;
                midiSess.connect().then(function (st) { try { window.MidiInput.toastOutcome(st); } catch (_) {} refresh(); });
            });
            var soundBtn = api.el('button', 'pn-chip');
            soundBtn.type = 'button';
            hub.sound = api.store.get('pn_midi_sound', true);
            var setS = function () { soundBtn.setAttribute('aria-pressed', hub.sound ? 'true' : 'false'); soundBtn.textContent = (hub.sound ? '🔊 ' : '🔇 ') + api.tt('p_sound'); };
            soundBtn.addEventListener('click', function () { hub.sound = !hub.sound; api.store.set('pn_midi_sound', hub.sound); setS(); });
            setS();
            bar.appendChild(midiBtn);
            bar.appendChild(soundBtn);
            midiBars.push(refresh);
            api.onCleanup(function () { midiBars = midiBars.filter(function (f) { return f !== refresh; }); });
        }
        var micBtn = null, micStatus = api.el('p', 'pn-mic-st');
        micStatus.hidden = true;
        var M = { listener: null, onListener: null };
        if (o.mic && MC) {
            micBtn = api.el('button', 'pn-chip');
            micBtn.type = 'button';
            micBtn.textContent = '🎤 ' + api.tt('p_mic');
            micBtn.addEventListener('click', function () {
                if (M.listener) return;
                micBtn.disabled = true;
                micStatus.hidden = false;
                micStatus.textContent = api.tt('p_mic_wait');
                MC.listen(api).then(function (L) {
                    if (!api.alive()) { L.stop(); return; }
                    M.listener = L;
                    micBtn.classList.add('is-on');
                    micBtn.textContent = '🎤 ' + api.tt('p_mic_on');
                    micStatus.textContent = api.tt(o.mic === 'onsets' ? 'p_mic_onsets' : 'p_mic_hint');
                    if (o.mic === 'notes') micNotes(api, hub, L);
                    if (M.onListener) M.onListener(L);
                }, function (err) {
                    if (!api.alive()) return;
                    micBtn.disabled = false;
                    micStatus.textContent = api.micError(err);
                });
            });
            bar.appendChild(micBtn);
        }
        host.appendChild(bar);
        host.appendChild(micStatus);
        if (midiBars.length) midiBars[midiBars.length - 1]();
        return M;
    }

    /* Пианино в комнате → события нот: устойчивая высота (MusicCore) —
       нажатие; новый удар по той же ноте (детектор ударов) — повтор; тишина —
       отпускание. Время — на часах страницы минус измеренная задержка
       распознавания (MIC_DELAY_S, docs/why/piano-course.md). */
    var MIC_DELAY_S = 0.13;   // медиана стенда: 127 мс от удара до устойчивой высоты
    function micNotes(api, hub, L) {
        var last = null, lastOnset = 0, emittedAt = 0, silentSince = 0;
        MC.onsetStream(L, function () { lastOnset = perfNow(); });
        MC.pitchStream(L, function (r) {
            var now = perfNow();
            if (!r) {
                if (last != null) {
                    if (!silentSince) silentSince = now;
                    if (now - silentSince > 0.25) { hub.off(last, 'mic', now); last = null; }
                }
                return;
            }
            silentSince = 0;
            var m = Math.round(r.midi);
            if (Math.abs((r.midi - m) * 100) > 45) return;
            var restruck = m === last && lastOnset > emittedAt + 0.05;
            if (m !== last || restruck) {
                if (last != null) hub.off(last, 'mic', now);
                last = m;
                emittedAt = now;
                hub.on(m, 'mic', now - MIC_DELAY_S);
            }
        }, { fmin: 55, fmax: 2100, holdMs: 200 });
    }

    /* Хрома аккорда с пианино в комнате. Те же ChromaMeter и BassMeter, что
       у MusicCore.chromaStream, но окно тише порога тишины ядра (SILENT_RMS —
       он подобран под гитару у самого телефона) поднимается до порога, если
       оно громче шума комнаты. Замер на синтезе рояля: тихий аккорд — 1 из 16
       без подъёма и 13 из 16 с ним; громкие окна подъём не трогает (подъём
       всего окна стенд тоже мерил: дальний аккорд хуже — шум поднимается
       вместе с ним). docs/why/piano-course.md. */
    var LIFT_FLOOR = 0.0006;
    function liftQuiet(x) {
        var s2 = 0, i;
        for (i = 0; i < x.length; i++) s2 += x[i] * x[i];
        var r = Math.sqrt(s2 / x.length), thr = MC.LIMITS.SILENT_RMS;
        if (r < LIFT_FLOOR || r >= thr) return x;
        var g = Math.min(16, 2 * thr / r), y = new Float32Array(x.length);
        for (i = 0; i < x.length; i++) y[i] = x[i] * g;
        return y;
    }
    function chordStream(L, fn) {
        var meter = new MC.analysis.ChromaMeter(L.sr), bm = new MC.analysis.BassMeter(L.sr);
        var frames = [], acc = 0, hop = Math.round(L.sr * 0.1);
        return L.onBlock(function (pcm, frame) {
            acc += pcm.length;
            if (acc < hop) return;
            acc = 0;
            var x = liftQuiet(L.latest(4096));
            var r = meter.frame(x, 0);
            var t = (frame + pcm.length) / L.sr;
            frames.push({ t: t, c: r.chroma, bass: r.chroma ? bm.read(x, 0, 4096) : null });
            while (frames.length && frames[0].t < t - 0.9) frames.shift();
            fn({ pooled: MC.analysis.pool(frames.map(function (q) { return q.c; })), bass: MC.analysis.modeOf(frames.map(function (q) { return q.bass; })), rms: r.rms });
        });
    }

    /* ── ноты: abcjs ────────────────────────────────────────────────── */

    var abcP = null;
    function abcReady() {
        if (window.ABCJS && window.ABCJS.renderAbc) return Promise.resolve(window.ABCJS);
        if (!abcP) {
            if (window.__vendorScript && CFG.abcjs) {
                abcP = window.__vendorScript(CFG.abcjs, { expect: 'ABCJS', mirror: CFG.abcjsMirror, label: 'abcjs' }).then(function () { return window.ABCJS; });
            } else {
                abcP = Promise.reject(new Error('abcjs_unavailable'));
            }
            abcP.catch(function () { abcP = null; });
        }
        return abcP;
    }
    var KEY_NAMES = { '-3': 'Eb', '-2': 'Bb', '-1': 'F', '0': 'C', '1': 'G', '2': 'D', '3': 'A' };
    var SHARP_ORDER = [3, 0, 4, 1, 5, 2, 6];   // F C G D A E B
    var FLAT_ORDER = [6, 2, 5, 1, 4, 0, 3];    // B E A D G C F
    function keyAcc(fifths) {
        var a = [0, 0, 0, 0, 0, 0, 0];
        for (var i = 0; i < Math.abs(fifths || 0); i++) a[(fifths > 0 ? SHARP_ORDER : FLAT_ORDER)[i]] = fifths > 0 ? 1 : -1;
        return a;
    }
    function abcPitch(p, state) {
        var acc = '';
        if (state) {
            if (state[p.letter] !== p.acc) {
                acc = p.acc === 1 ? '^' : p.acc === -1 ? '_' : '=';
                state[p.letter] = p.acc;
            }
        } else if (p.acc) acc = p.acc === 1 ? '^' : '_';
        var L2 = LET[p.letter];
        var s = p.oct >= 5 ? L2.toLowerCase() + new Array(p.oct - 4).join("'") : L2 + new Array(5 - p.oct).join(',');
        return acc + s;
    }
    function abcLen(beats, unit) {
        var n = Math.round(beats * unit);
        return n === 1 ? '' : String(n);
    }
    function q(s) { return String(s).replace(/"/g, "'"); }
    /*
     * Голос → строка ABC и список «токенов» (нота/пауза с индексом события).
     * evs: [{t, d, n: [имена], f?, i}] одного голоса; пустые места — паузы.
     */
    function voiceAbc(evs, total, measure, fifths, opts) {
        var unit = 2;                              // L:1/8 — доля (четверть) = 2
        var out = [], tokens = [], pos = 0, inBar = 0;
        var state = keyAcc(fifths);
        function bar() {
            out.push('|');
            state = keyAcc(fifths);
            inBar = 0;
        }
        function rest(d) {
            while (d > 1e-6) {
                var room = measure ? measure - inBar : d;
                var piece = Math.min(d, room);
                out.push('z' + abcLen(piece, unit));
                tokens.push({ rest: true, i: null });
                inBar += piece; pos += piece; d -= piece;
                if (measure && inBar >= measure - 1e-6) bar();
            }
        }
        evs.slice().sort(function (a, b) { return a.t - b.t; }).forEach(function (e) {
            if (e.t > pos + 1e-6) rest(e.t - pos);
            var ps = (e.n || []).map(parse).filter(Boolean);
            var deco = '';
            if (opts.fingers && e.f != null) deco += '!' + (Array.isArray(e.f) ? e.f[0] : e.f) + '!';
            var ann = '';
            if (opts.labels && ps.length === 1) ann = '"_' + q(label(ps[0])) + '"';
            var body = ps.length > 1 ? '[' + ps.map(function (p) { return abcPitch(p, state); }).join('') + ']' : abcPitch(ps[0], state);
            out.push(ann + deco + body + abcLen(e.d, unit) + (opts.spaced ? ' ' : ''));
            tokens.push({ rest: false, i: e.i });
            inBar += e.d; pos += e.d;
            if (measure && inBar >= measure - 1e-6) bar();
        });
        if (total > pos + 1e-6) rest(total - pos);
        var s = out.join(' ').replace(/\|\s*$/, '');
        return { abc: s + ' |]', tokens: tokens };
    }
    /*
     * spec: {clef: 'treble'|'bass'|'grand'|'perc', key, time: [4,4]|null,
     *        events: [{t, d, n, h, f}], labels, fingers}
     */
    function toAbc(spec) {
        var time = spec.time || null;
        var measure = time ? time[0] * (4 / time[1]) : 0;
        var evs = (spec.events || []).map(function (e, i) { return Object.assign({ i: i }, e); });
        var total = 0;
        evs.forEach(function (e) { total = Math.max(total, e.t + e.d); });
        if (measure) total = Math.ceil(total / measure - 1e-6) * measure;
        var head = ['X:1', 'M:' + (time ? time[0] + '/' + time[1] : 'none'), 'L:1/8'];
        var fifths = spec.key || 0;
        var o = { labels: !!spec.labels, fingers: !!spec.fingers, spaced: !time };
        if (spec.clef === 'grand') {
            var rh = evs.filter(function (e) { return e.h !== 'L'; }), lh = evs.filter(function (e) { return e.h === 'L'; });
            var a = voiceAbc(rh, total, measure, fifths, o), b = voiceAbc(lh, total, measure, fifths, o);
            head.push('%%score {RH | LH}');
            head.push('K:' + KEY_NAMES[fifths]);
            head.push('V:RH clef=treble');
            head.push('V:LH clef=bass');
            return { abc: head.join('\n') + '\n[V:RH] ' + a.abc + '\n[V:LH] ' + b.abc + '\n', voices: [a.tokens, b.tokens] };
        }
        var v = voiceAbc(evs, total, measure, fifths, o);
        var clef = spec.clef === 'perc' ? 'perc stafflines=1' : spec.clef === 'bass' ? 'bass' : 'treble';
        head.push('K:' + KEY_NAMES[fifths] + ' clef=' + clef);
        return { abc: head.join('\n') + '\n' + v.abc + '\n', voices: [v.tokens] };
    }
    /*
     * Нарисовать ноты в host. → Promise<{mark(eventIndex, cls), unmark(cls)}>.
     * Без abcjs (не загрузился) — строка имён нот, шаг остаётся проходимым.
     */
    function renderStaff(host, api, spec) {
        var box = api.el('div', 'pn-staff');
        host.appendChild(box);
        var built = toAbc(spec);
        var byEvent = {};
        var api2 = {
            box: box,
            mark: function (i, cls) { (byEvent[i] || []).forEach(function (n) { try { n.classList.add(cls); } catch (_) {} }); },
            unmark: function (cls) { Object.keys(byEvent).forEach(function (k) { byEvent[k].forEach(function (n) { try { n.classList.remove(cls); } catch (_) {} }); }); }
        };
        function fallback() {
            box.classList.add('is-text');
            box.textContent = (spec.events || []).map(function (e) { return (e.n || []).map(function (x) { return label(parse(x)); }).join('+'); }).join(' · ');
            return api2;
        }
        return abcReady().then(function (ABCJS) {
            if (!api.alive()) return api2;
            var w = Math.max(240, Math.min(820, (box.clientWidth || host.clientWidth || 600) - 8));
            /* Одна нота или аккорд — в своём размере и крупно (адаптивный
               режим растянул бы короткую строку и оставил пустое поле);
               партия — по ширине карточки, с переносом тактов. */
            var opts = spec.fixed ? {
                add_classes: true,
                scale: spec.scale || 1.7,
                staffwidth: spec.width || 120,
                paddingleft: 0, paddingright: 0, paddingtop: 0, paddingbottom: 0,
                foregroundColor: 'currentColor'
            } : {
                add_classes: true,
                responsive: 'resize',
                staffwidth: w,
                paddingleft: 0, paddingright: 0, paddingtop: 4, paddingbottom: 4,
                wrap: spec.time ? { minSpacing: 1.6, maxSpacing: 2.8, preferredMeasuresPerLine: w < 420 ? 2 : 4 } : undefined,
                foregroundColor: 'currentColor'
            };
            if (spec.fixed) box.classList.add('is-fixed');
            var vo = ABCJS.renderAbc(box, built.abc, opts);
            var tune = vo && vo[0];
            if (!tune || !tune.lines) return fallback();
            var seq = built.voices.map(function () { return []; });
            tune.lines.forEach(function (line) {
                (line.staff || []).forEach(function (st, si) {
                    var voice = st.voices && st.voices[0];
                    if (!voice || !seq[si]) return;
                    voice.forEach(function (el) {
                        if (el.el_type === 'note' && el.abselem && el.abselem.elemset) seq[si].push(el.abselem.elemset);
                    });
                });
            });
            built.voices.forEach(function (tokens, vi) {
                tokens.forEach(function (tok, k) {
                    if (tok.i == null || !seq[vi][k]) return;
                    byEvent[tok.i] = (byEvent[tok.i] || []).concat(seq[vi][k]);
                });
            });
            var svg = box.querySelector('svg');
            if (svg) {
                svg.setAttribute('role', 'img');
                svg.setAttribute('aria-label', (spec.events || []).map(function (e) { return (e.n || []).map(function (x) { return label(parse(x)); }).join('+'); }).join(', '));
            }
            return api2;
        }, function () { return fallback(); });
    }

    /* ── рисунки: ноты и клавиатура ─────────────────────────────────── */

    function figEvents(fig) {
        if (fig.events) return fig.events;
        var t = 0;
        return (fig.notes || []).map(function (n) {
            var e = { t: t, d: fig.d || 4, n: Array.isArray(n) ? n : [n] };
            t += e.d;
            return e;
        });
    }
    K.figure('staff', function (box, fig, step, api) {
        var evs = figEvents(fig);
        renderStaff(box, api, { clef: fig.clef || 'treble', key: fig.key || 0, time: fig.time || null, events: evs, labels: !!fig.labels, fingers: !!fig.fingers, fixed: !fig.time, scale: fig.scale || (evs.length > 6 ? 1.3 : 1.6), width: fig.width || Math.min(560, 60 + evs.length * 34) });
    });
    K.figure('keys', function (box, fig, step, api) {
        var lo = midiOf(fig.range ? fig.range[0] : 'C4'), hi = midiOf(fig.range ? fig.range[1] : 'C5');
        var dummy = { on: function () {}, off: function () {}, listen: function () {} };
        var kb = Keyboard(api, dummy, { lo: lo, hi: hi, labels: fig.labels || 'marked' });
        kb.el.classList.add('is-static');
        (fig.mark || []).forEach(function (n) { kb.mark(midiOf(n), 'is-target'); });
        var fingers = fig.fingers || {};
        Object.keys(fingers).forEach(function (n) {
            var el = kb.el.querySelector('.pn-key[data-m="' + midiOf(n) + '"]');
            if (el) { var f = api.el('b', 'pn-finger', String(fingers[n])); el.appendChild(f); }
        });
        kb.relabel(null);
        box.appendChild(kb.el);
    });

    /* общий кусок: статус и кнопка «Послушать» */
    function status(host, api, text) {
        var p = api.el('p', 'pn-st');
        p.setAttribute('aria-live', 'polite');
        p.textContent = text || '';
        host.appendChild(p);
        return {
            el: p,
            set: function (s, cls) { p.textContent = s; p.className = 'pn-st' + (cls ? ' ' + cls : ''); }
        };
    }
    function rangeOf(step, notes, padLo, padHi) {
        if (step.range) return [midiOf(step.range[0]), midiOf(step.range[1])];
        var ms = notes.filter(function (x) { return x != null; });
        var lo = Math.min.apply(null, ms), hi = Math.max.apply(null, ms);
        lo = lo - pcOf(lo) - (padLo || 0);                    // от До
        hi = hi + (11 - pcOf(hi)) + (padHi || 0) + 1;         // до До выше
        if (hi - lo < 12) hi = lo + 12;
        return [lo, hi];
    }

    /* ── найти клавишу ──────────────────────────────────────────────── */

    K.step('piano-find', {
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var lo = midiOf(step.range ? step.range[0] : 'C4'), hi = midiOf(step.range ? step.range[1] : 'B4');
            var targets = (step.targets || []).map(midiOf);
            if (step.pc) {
                var want = parse(step.pc + '4');
                for (var m = lo; m <= hi; m++) if (pcOf(m) === pcOf(want.midi)) targets.push(m);
            }
            var hub = Hub(api);
            var kb = Keyboard(api, hub, { lo: lo, hi: hi, labels: step.labels || 'none' });
            host.appendChild(kb.el);
            var km = bindComputerKeys(api, hub, kb.lo, kb.hi, kb.whites);
            inputBar(host, api, hub, kb, km, { mic: step.mic === false ? false : 'notes' });
            var st = status(host, api, api.tt('p_find_left', { n: targets.length }));
            var found = {}, misses = 0, done = false, idx = 0;
            if (step.sequence && targets.length) kb.mark(targets[0], 'is-next');
            hub.listen(function (type, mm) {
                if (type !== 'on' || done) return;
                var ok = step.sequence ? mm === targets[idx] : targets.indexOf(mm) >= 0 && !found[mm];
                var any = !step.exact && step.pc && targets.indexOf(mm) >= 0;
                if (ok || (any && !found[mm])) {
                    found[mm] = 1;
                    kb.mark(mm, 'is-ok');
                    if (step.sequence) { kb.unmark('is-next'); idx++; if (targets[idx] != null) kb.mark(targets[idx], 'is-next'); }
                    var left = targets.filter(function (x) { return !found[x]; }).length;
                    if (left === 0) {
                        done = true;
                        st.set(api.tt('p_find_done'), 'is-good');
                        setTimeout(function () {
                            if (!api.alive()) return;
                            api.answer({ correct: misses <= (step.allow != null ? step.allow : 1), score: Math.max(0, 1 - misses * 0.2), explain: t.explain || null });
                        }, 350);
                        return;
                    }
                    st.set(api.tt('p_find_left', { n: left }));
                } else if (!found[mm]) {
                    misses++;
                    kb.flash(mm, 'is-bad');
                    st.set(api.tt('p_find_wrong', { heard: label(mm) }), 'is-bad');
                }
            });
            api.secondary(api.tt('p_show'), function () {
                targets.forEach(function (x) { if (!found[x]) kb.mark(x, 'is-show'); });
                misses += 2;
            });
        }
    });

    /* ── прочитать ноту ─────────────────────────────────────────────── */

    function shuffledPool(pool, count) {
        var out = [], last = null;
        for (var i = 0; i < count; i++) {
            var choices = pool.filter(function (x) { return x !== last; });
            var pick = choices[Math.floor(Math.random() * choices.length)] || pool[0];
            out.push(pick);
            last = pick;
        }
        return out;
    }
    K.step('staff-read', {
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var pool = step.pool || ['C4', 'D4', 'E4', 'F4', 'G4'];
            var list = step.order === 'given' ? pool.slice(0, step.count || pool.length) : shuffledPool(pool, step.count || 8);
            var r = rangeOf(step, pool.map(midiOf));
            var count = api.el('p', 'pn-count');
            host.appendChild(count);
            var staffHost = api.el('div', 'pn-read');
            host.appendChild(staffHost);
            var hub = Hub(api);
            var kb = Keyboard(api, hub, { lo: r[0], hi: r[1], labels: step.labels || 'none' });
            host.appendChild(kb.el);
            var km = bindComputerKeys(api, hub, kb.lo, kb.hi, kb.whites);
            inputBar(host, api, hub, kb, km, { mic: step.mic === false ? false : 'notes' });
            var st = status(host, api, '');
            var i = 0, firstTry = 0, wrongHere = 0, busy = false, done = false;
            function show() {
                staffHost.innerHTML = '';
                count.textContent = api.tt('p_read_count', { n: i + 1, total: list.length });
                renderStaff(staffHost, api, { clef: step.clef || 'treble', key: step.key || 0, time: null, fixed: true, events: [{ t: 0, d: 4, n: [list[i]] }] });
                kb.unmark('is-show');
                wrongHere = 0;
                st.set(api.tt('p_read_play'));
            }
            hub.listen(function (type, mm) {
                if (type !== 'on' || busy || done) return;
                var want = parse(list[i]);
                var ok = step.exact === false ? pcOf(mm) === pcOf(want.midi) : mm === want.midi;
                if (ok) {
                    if (wrongHere === 0) firstTry++;
                    kb.flash(mm, 'is-ok', 450);
                    st.set(api.tt('p_read_ok', { note: label(want) }), 'is-good');
                    busy = true;
                    setTimeout(function () {
                        busy = false;
                        if (!api.alive()) return;
                        i++;
                        if (i >= list.length) {
                            done = true;
                            var score = firstTry / list.length;
                            api.answer({ correct: score >= (step.pass || 0.8) - 1e-9, score: score, explain: (api.tt('p_read_score', { n: firstTry, total: list.length }) + (t.explain ? '\n\n' + t.explain : '')) });
                            return;
                        }
                        show();
                    }, 420);
                } else {
                    wrongHere++;
                    kb.flash(mm, 'is-bad');
                    var near = step.exact !== false && pcOf(mm) === pcOf(want.midi);
                    st.set(near ? api.tt('p_read_octave', { heard: label(mm) }) : api.tt('p_read_wrong', { heard: labelLike(mm, want) }), 'is-bad');
                    if (wrongHere >= 2) kb.mark(want.midi, 'is-show');
                }
            });
            show();
        }
    });

    /* ── сыграть по нотам ───────────────────────────────────────────── */

    function expand(step) {
        return (step.events || []).map(function (e, i) {
            return { i: i, t: e.t, d: e.d, n: (e.n || []).map(midiOf), names: e.n || [], h: e.h || 'R', f: e.f };
        });
    }
    var TOL_OK = 0.13, TOL_OFF = 0.32;
    K.step('piano-play', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var evs = expand(step);
            var all = [];
            evs.forEach(function (e) { all = all.concat(e.n); });
            var r = rangeOf(step, all);
            var tempo = step.mode === 'tempo';
            var bpm = step.bpm || 72, slower = 1;
            var staff = null;
            var staffHost = api.el('div', 'pn-score');
            host.appendChild(staffHost);
            renderStaff(staffHost, api, { clef: step.clef || 'treble', key: step.key || 0, time: step.time || [4, 4], events: step.events, labels: !!step.labels, fingers: step.fingers !== false }).then(function (s) { staff = s; mark(); });
            var hub = Hub(api);
            var kb = Keyboard(api, hub, { lo: r[0], hi: r[1], labels: step.keyLabels || (step.labels ? 'marked' : 'c'), lane: tempo });
            host.appendChild(kb.el);
            var km = bindComputerKeys(api, hub, kb.lo, kb.hi, kb.whites);
            inputBar(host, api, hub, kb, km, { mic: step.mic === false ? false : 'notes' });
            var st = status(host, api, '');
            var best = 0, attempts = 0;

            function mark() {
                if (!staff) return;
                staff.unmark('pn-cur');
                if (!tempo && groups && cur < groups.length) groups[cur].idx.forEach(function (ei) { staff.mark(ei, 'pn-cur'); });
            }
            function listen() {
                return api.audioCtx().then(function (ctx) {
                    var at = ctx.currentTime + 0.1;
                    var end = schedule(ctx, evs, bpm * slower, at);
                    var spb = 60 / (bpm * slower);
                    evs.forEach(function (e) {
                        setTimeout(function () { if (api.alive() && staff) { staff.unmark('pn-play'); staff.mark(e.i, 'pn-play'); } }, (at - ctx.currentTime + e.t * spb) * 1000);
                    });
                    return wait((end - ctx.currentTime + 0.2) * 1000).then(function () { if (staff) staff.unmark('pn-play'); });
                });
            }
            var listenBtn = api.el('button', 'lg-btn lg-btn-ghost lg-btn-sm', '▶ ' + api.tt('p_listen'));
            listenBtn.type = 'button';
            listenBtn.addEventListener('click', function () {
                listenBtn.disabled = true;
                listen().catch(function () {}).then(function () { listenBtn.disabled = false; });
            });
            var row = api.el('div', 'crs-acts');
            row.appendChild(listenBtn);
            host.insertBefore(row, staffHost);

            /* ── режим «с ожиданием» ──
               Цель — всё, что звучит в одну долю: нота правой и нота левой
               руки на одном времени — одна цель (нажать обе). */
            var groups = [];
            evs.slice().sort(function (a, b) { return a.t - b.t || (a.h === 'L' ? 1 : 0) - (b.h === 'L' ? 1 : 0); }).forEach(function (e) {
                var g = groups.length && Math.abs(groups[groups.length - 1].t - e.t) < 1e-6 ? groups[groups.length - 1] : null;
                if (!g) { g = { t: e.t, idx: [], n: [], names: [] }; groups.push(g); }
                g.idx.push(e.i);
                e.n.forEach(function (m, k) { if (g.n.indexOf(m) < 0) { g.n.push(m); g.names.push(e.names[k]); } });
            });
            function gName(g) { return g.names.map(function (x) { return label(parse(x)); }).join('+'); }
            var cur = 0, dirty = {}, got = {}, chordAt = 0;
            function targetKeys() {
                kb.unmark('is-target');
                if (cur < groups.length) groups[cur].n.forEach(function (m) { kb.mark(m, 'is-target'); });
                kb.relabel(km);
            }
            function waitDone() {
                var clean = groups.filter(function (g, k) { return !dirty[k]; }).length;
                var score = clean / groups.length;
                attempts++;
                best = Math.max(best, score);
                kb.unmark('is-target');
                st.set(api.tt('p_wait_done', { ok: clean, total: groups.length }), score >= (step.pass || 0.8) ? 'is-good' : 'is-bad');
                finish(score);
            }
            function resetWait() {
                cur = 0; dirty = {}; got = {};
                if (staff) { staff.unmark('pn-ok'); staff.unmark('pn-off'); staff.unmark('pn-miss'); }
                mark(); targetKeys();
                st.set(api.tt('p_wait_go', { note: groups.length ? gName(groups[0]) : '' }));
            }
            /* ── режим «в темпе» ── */
            var run = null;
            function tempoStart() {
                attempts++;
                api.primary(api.tt('p_playing'), null, true);
                if (staff) { staff.unmark('pn-ok'); staff.unmark('pn-off'); staff.unmark('pn-miss'); }
                return api.audioCtx().then(function (ctx) {
                    if (!api.alive()) return;
                    var b = bpm * slower, spb = 60 / b;
                    var time = step.time || [4, 4];
                    var beats = time[0];
                    var totalBeats = 0;
                    evs.forEach(function (e) { totalBeats = Math.max(totalBeats, e.t + e.d); });
                    var bars = Math.ceil(totalBeats / beats);
                    var met = MC ? MC.metronome(ctx, { bpm: b, beats: beats, countIn: beats, bars: bars, volume: 0.45 }) : null;
                    var t0 = met ? met.start() : ctx.currentTime + 0.3 + beats * spb;
                    /* Часы страницы ↔ слышимое время контекста. Смещение берётся
                       заново при каждом чтении (beatPerf): часы звуковой карты
                       и часы страницы расходятся, за минуту пьесы — заметно. */
                    var off0 = heardMinusPerf(ctx);
                    var startPerf = t0 - off0;
                    run = { ctx: ctx, met: met, spb: spb, t0: t0, startPerf: startPerf, off0: off0, presses: [], verdict: {}, totalBeats: totalBeats, done: false };
                    run.beatPerf = function (beat) { return run.t0 + beat * run.spb - heardMinusPerf(run.ctx); };
                    lastRun = run;
                    st.set(api.tt('p_count_in'));
                    api.onCleanup(function () { if (met) met.stop(); });
                    drawLane();
                    var endPerf = startPerf + (totalBeats + 1) * spb;
                    var poll = setInterval(function () {
                        if (!api.alive() || !run || run.done) { clearInterval(poll); return; }
                        var now = perfNow();
                        if (now >= startPerf && now < startPerf + spb) st.set(api.tt('p_go'));
                        judgeLive(now);
                        if (now > endPerf) { clearInterval(poll); tempoEnd(); }
                    }, 60);
                });
            }
            function judgeLive(now) {
                evs.forEach(function (e) {
                    e.n.forEach(function (m, k) {
                        var key = e.i + ':' + k;
                        if (run.verdict[key]) return;
                        var te = run.beatPerf(e.t);
                        if (now > te + TOL_OFF) { run.verdict[key] = 'miss'; paint(e.i); }
                    });
                });
            }
            function paint(ei) {
                if (!staff || !run) return;
                var e = evs[ei];
                var vs = e.n.map(function (_, k) { return run.verdict[ei + ':' + k]; });
                if (vs.some(function (v) { return !v; })) return;
                var cls = vs.every(function (v) { return v === 'ok'; }) ? 'pn-ok' : vs.some(function (v) { return v === 'miss'; }) ? 'pn-miss' : 'pn-off';
                staff.mark(ei, cls);
            }
            function onTempoPress(m, tp) {
                if (!run || run.done) return;
                var start = run.beatPerf(0);
                if (tp < start - TOL_OFF) return;                    // во время отсчёта
                var best1 = null, bd = 1e9;
                evs.forEach(function (e) {
                    e.n.forEach(function (mm, k) {
                        var key = e.i + ':' + k;
                        if (mm !== m || run.verdict[key]) return;
                        var d = tp - (start + e.t * run.spb);
                        if (Math.abs(d) < Math.abs(bd)) { bd = d; best1 = { e: e, key: key }; }
                    });
                });
                if (best1 && Math.abs(bd) <= TOL_OFF) {
                    run.verdict[best1.key] = Math.abs(bd) <= TOL_OK ? 'ok' : 'off';
                    run.presses.push({ m: m, d: bd, hit: true });
                    paint(best1.e.i);
                    kb.flash(m, Math.abs(bd) <= TOL_OK ? 'is-ok' : 'is-warn', 300);
                } else {
                    run.presses.push({ m: m, hit: false });
                    kb.flash(m, 'is-bad', 300);
                }
            }
            function tempoEnd() {
                if (!run || run.done) return;
                run.done = true;
                if (run.met) run.met.stop();
                var c = { ok: 0, off: 0, miss: 0 }, n = 0;
                Object.keys(run.verdict).forEach(function (k) { c[run.verdict[k]]++; n++; });
                evs.forEach(function (e) { e.n.forEach(function (_, k) { if (!run.verdict[e.i + ':' + k]) { c.miss++; n++; } }); });
                var extra = run.presses.filter(function (p) { return !p.hit; }).length;
                var score = n ? Math.max(0, (c.ok + 0.5 * c.off) / n - 0.05 * extra) : 0;
                best = Math.max(best, score);
                if (!run.presses.length) {
                    st.set(api.tt('p_silence'), 'is-bad');
                } else {
                    var hits = run.presses.filter(function (p) { return p.hit; });
                    var mean = hits.length ? hits.reduce(function (a, p) { return a + p.d; }, 0) / hits.length : 0;
                    st.set(api.tt('p_tempo_result', { ok: c.ok, off: c.off, miss: c.miss, extra: extra }) + (hits.length >= 3 && Math.abs(mean) > 0.06 ? ' ' + api.tt(mean > 0 ? 'p_late' : 'p_early', { ms: Math.round(Math.abs(mean) * 1000) }) : ''), score >= (step.pass || 0.8) ? 'is-good' : 'is-bad');
                }
                ae('course_piano_tempo', { step: step.id, score: Math.round(score * 100), src: hub.used().join(','), slower: slower < 1 ? 1 : 0 });
                finish(score);
            }
            function drawLane() {
                var c = kb.lane;
                if (!c || !run) return;
                var g = c.getContext('2d');
                function frame() {
                    if (!api.alive() || !run || run.done) { if (g) g.clearRect(0, 0, c.width, c.height); return; }
                    var w = c.clientWidth, h = c.clientHeight, dpr = window.devicePixelRatio || 1;
                    if (c.width !== Math.round(w * dpr)) { c.width = Math.round(w * dpr); c.height = Math.round(h * dpr); }
                    g.setTransform(dpr, 0, 0, dpr, 0, 0);
                    g.clearRect(0, 0, w, h);
                    var now = perfNow(), ahead = 3 * run.spb, kw = kb.keyW() * w, start = run.beatPerf(0);
                    var css = getComputedStyle(c);
                    var colR = css.getPropertyValue('--pn-r') || '#7aa7ff', colL = css.getPropertyValue('--pn-l') || '#f59e0b';
                    evs.forEach(function (e) {
                        var ts = start + e.t * run.spb, te = ts + e.d * run.spb;
                        if (te < now - 0.2 || ts > now + ahead) return;
                        var y1 = h - (ts - now) / ahead * h, y0 = h - (te - now) / ahead * h;
                        e.n.forEach(function (m) {
                            var x = kb.x(m);
                            if (x < 0) return;
                            g.fillStyle = e.h === 'L' ? colL : colR;
                            var bw = isBlack(m) ? kw * 0.55 : kw * 0.8;
                            g.globalAlpha = ts < now - TOL_OFF ? 0.35 : 0.9;
                            g.fillRect(x * w - bw / 2, Math.max(0, y0), bw, Math.max(4, Math.min(h, y1) - Math.max(0, y0)));
                        });
                    });
                    g.globalAlpha = 1;
                    requestAnimationFrame(frame);
                }
                requestAnimationFrame(frame);
            }
            function finish(score) {
                var pass = step.pass || 0.8;
                if (score >= pass - 1e-9) {
                    api.primary(api.t('next'), function () { api.answer({ correct: true, score: best, explain: t.explain || null }); });
                    api.secondary(api.tt('p_again'), start);
                } else {
                    api.primary(api.tt('p_again'), start);
                    api.secondary(api.tt('p_accept'), function () { api.answer({ correct: false, score: best, explain: t.explain || null }); });
                    if (tempo && slower === 1 && attempts >= 1) {
                        var sl = api.el('button', 'lg-btn lg-btn-ghost lg-btn-sm', api.tt('p_slower'));
                        sl.type = 'button';
                        sl.addEventListener('click', function () { slower = 0.75; sl.remove(); start(); });
                        row.appendChild(sl);
                    }
                }
            }
            function start() {
                if (tempo) { tempoStart(); return; }
                resetWait();
                api.primary(api.tt('p_restart'), resetWait);
                api.secondary(null, null);
            }
            hub.listen(function (type, m, src, tp) {
                if (type !== 'on') return;
                if (tempo) { onTempoPress(m, tp); return; }
                if (cur >= groups.length) return;
                var g = groups[cur];
                if (g.n.indexOf(m) >= 0) {
                    if (!Object.keys(got).length) chordAt = tp;
                    got[m] = 1;
                    /* ноты цели держат вместе — или берут друг за другом не дольше
                       чем за 1.2 с (одна рука, потом другая) */
                    var need = g.n.filter(function (x) { return !got[x] && hub.held().indexOf(x) < 0; });
                    if (need.length && tp - chordAt > 1.2) { got = {}; got[m] = 1; chordAt = tp; need = g.n.filter(function (x) { return !got[x]; }); }
                    if (!need.length) {
                        if (staff) g.idx.forEach(function (ei) { staff.mark(ei, dirty[cur] ? 'pn-off' : 'pn-ok'); });
                        got = {};
                        cur++;
                        mark();
                        targetKeys();
                        if (cur >= groups.length) { waitDone(); return; }
                        st.set(api.tt('p_wait_next', { note: gName(groups[cur]) }));
                    }
                } else {
                    dirty[cur] = 1;
                    kb.flash(m, 'is-bad');
                    st.set(api.tt('p_wait_wrong', { heard: labelLike(m, parse(g.names[0])), need: gName(g) }), 'is-bad');
                }
            });
            if (tempo) {
                st.set(api.tt('p_tempo_intro', { bpm: bpm }));
                api.primary(api.tt('p_start_tempo'), start);
            } else {
                /* «С ожиданием» начинается сразу: подсвечена первая клавиша,
                   курс ждёт её — отдельная кнопка «Играть» тут лишний шаг. */
                start();
            }
        }
    });

    /* Слышимое время контекста минус часы страницы (сек). Нажатие пальца
       — отклик на то, что человек СЛЫШИТ, а не на то, что граф отправил. */
    function heardMinusPerf(ctx) {
        var now = perfNow();
        try {
            var ts = ctx.getOutputTimestamp && ctx.getOutputTimestamp();
            if (ts && ts.contextTime > 0 && ts.performanceTime > 0) {
                return ts.contextTime + (now * 1000 - ts.performanceTime) / 1000 - now;
            }
        } catch (_) {}
        var lag = 0;
        try { lag = window.AudibleClock ? window.AudibleClock.lagS(ctx) : (ctx.outputLatency || 0) + (ctx.baseLatency || 0); } catch (_) {}
        return ctx.currentTime - lag - now;
    }

    /* ── ритм ───────────────────────────────────────────────────────── */

    K.step('rhythm-tap', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var time = step.time || [4, 4], beats = time[0];
            var pat = step.pattern || [1, 1, 1, 1];
            var reps = step.repeat || 1;
            var ev = [], pos = 0;
            for (var rr = 0; rr < reps; rr++) {
                pat.forEach(function (d) { if (d > 0) ev.push({ t: pos, d: d, n: ['B4'] }); pos += Math.abs(d); });
            }
            var total = pos;
            var nHost = api.el('div', 'pn-score');
            host.appendChild(nHost);
            var staff = null;
            renderStaff(nHost, api, { clef: 'perc', key: 0, time: time, events: ev }).then(function (s) { staff = s; });
            var pad = api.el('button', 'pn-pad', api.tt('p_tap'));
            pad.type = 'button';
            host.appendChild(pad);
            var laneEl = api.el('div', 'pn-rlane');
            host.appendChild(laneEl);
            var hub = Hub(api);
            var st = status(host, api, api.tt('p_rhythm_intro', { bpm: step.bpm || 80 }));
            var M = inputBar(host, api, hub, { el: api.el('div'), relabel: function () {} }, null, { mic: step.mic === false ? false : 'onsets' });
            var run = null, best = 0, attempts = 0;
            function tap(tp) {
                if (!run || run.done) return;
                run.taps.push(tp);
                pad.classList.add('is-hit');
                setTimeout(function () { pad.classList.remove('is-hit'); }, 90);
            }
            pad.addEventListener('pointerdown', function (e) { e.preventDefault(); tap(perfNow()); });
            function kd(e) {
                if (e.code !== 'Space' || e.repeat) return;
                if (window.__keyOwnedByFocus && window.__keyOwnedByFocus(e)) return;
                if (!run || run.done) return;
                e.preventDefault();
                tap(perfNow());
            }
            document.addEventListener('keydown', kd);
            api.onCleanup(function () { document.removeEventListener('keydown', kd); });
            hub.listen(function (type, m, src, tp) { if (type === 'on') tap(tp); });
            M.onListener = function (L) {
                MC.onsetStream(L, function (gt) {
                    if (!run || run.done) return;
                    var lag = MC.latency.get(api, run.ctx).s;
                    tap(gt - lag - run.off);
                });
            };
            function start() {
                attempts++;
                api.primary(api.tt('p_playing'), null, true);
                api.audioCtx().then(function (ctx) {
                    if (!api.alive()) return;
                    var bpm = step.bpm || 80, spb = 60 / bpm;
                    var met = MC.metronome(ctx, { bpm: bpm, beats: beats, countIn: step.countIn || beats, bars: Math.ceil(total / beats), volume: 0.5 });
                    var t0 = met.start();
                    var off = heardMinusPerf(ctx);
                    run = { ctx: ctx, met: met, spb: spb, start: t0 - off, taps: [], done: false, off: off, kind: 'rhythm', expected: ev.map(function (e) { return e.t; }) };
                    run.beatPerf = function (beat) { return t0 + beat * spb - heardMinusPerf(ctx); };
                    lastRun = run;
                    api.onCleanup(function () { met.stop(); });
                    st.set(api.tt('p_count_in'));
                    var end = run.start + (total + 0.6) * spb;
                    var iv = setInterval(function () {
                        if (!api.alive() || run.done) { clearInterval(iv); return; }
                        var now = perfNow();
                        if (now >= run.start && now < run.start + spb) st.set(api.tt('p_go'));
                        if (now > end) { clearInterval(iv); finishRun(); }
                    }, 50);
                });
            }
            function finishRun() {
                run.done = true;
                run.met.stop();
                var exp = ev.map(function (e) { return run.start + e.t * run.spb; });
                var j = MC.analysis.judgeRhythm(exp, run.taps, step.tol || 0.12);
                best = Math.max(best, j.score);
                laneEl.innerHTML = '';
                var span = (total) * run.spb;
                j.marks.forEach(function (mk) {
                    var x = api.el('i', mk.hit ? '' : 'is-miss');
                    x.style.left = ((mk.t - run.start) / span * 100) + '%';
                    laneEl.appendChild(x);
                });
                run.taps.forEach(function (tp) {
                    if (tp < run.start - 0.3) return;
                    var b = api.el('b');
                    b.style.left = Math.max(0, Math.min(100, (tp - run.start) / span * 100)) + '%';
                    laneEl.appendChild(b);
                });
                if (staff) {
                    staff.unmark('pn-ok'); staff.unmark('pn-miss');
                    j.marks.forEach(function (mk, k) { staff.mark(k, mk.hit ? 'pn-ok' : 'pn-miss'); });
                }
                var dir = j.mean > 0.03 ? api.tt('p_r_late') : j.mean < -0.03 ? api.tt('p_r_early') : '';
                st.set(api.tt('p_r_result', { hits: j.hits, total: j.total, extra: j.extras }) + (dir && j.hits >= 3 ? ' ' + api.tt('p_r_mean', { ms: Math.round(Math.abs(j.mean) * 1000), dir: dir }) : ''), j.score >= (step.pass || 0.75) ? 'is-good' : 'is-bad');
                var pass = step.pass || 0.75;
                if (j.score >= pass - 1e-9) {
                    api.primary(api.t('next'), function () { api.answer({ correct: true, score: best, explain: t.explain || null }); });
                    api.secondary(api.tt('p_again'), start);
                } else {
                    api.primary(api.tt('p_again'), start);
                    api.secondary(api.tt('p_accept'), function () { api.answer({ correct: false, score: best, explain: t.explain || null }); });
                }
            }
            if (!MC) { api.primary(api.t('next'), function () { api.skip(); }); return; }
            api.primary(api.tt('p_start_rhythm'), start);
        }
    });

    /* ── аккорд ─────────────────────────────────────────────────────── */

    K.step('piano-chord', {
        reviewable: false,
        mount: function (host, step, api) {
            var t = T(step);
            if (t.prompt) host.appendChild(api.richEl('p', 'crs-prompt', t.prompt));
            var chords = (step.chords || []).map(function (c) {
                var ms = c.notes.map(midiOf);
                var info = MC && MC.chord(c.name) || null;
                return { name: c.name, notes: c.notes, midis: ms, pcs: ms.map(pcOf).filter(function (x, k, a) { return a.indexOf(x) === k; }), info: info };
            });
            var all = [];
            chords.forEach(function (c) { all = all.concat(c.midis); });
            var r = rangeOf(step, all);
            var head = api.el('p', 'pn-chord-name');
            host.appendChild(head);
            var staffHost = api.el('div', 'pn-read');
            host.appendChild(staffHost);
            var hub = Hub(api);
            var kb = Keyboard(api, hub, { lo: r[0], hi: r[1], labels: step.labels || 'marked', latch: true });
            host.appendChild(kb.el);
            var km = bindComputerKeys(api, hub, kb.lo, kb.hi, kb.whites);
            var M = inputBar(host, api, hub, kb, km, { mic: step.mic === false ? false : 'chord' });
            if (!coarse()) host.appendChild(api.el('p', 'crs-hint', api.tt('p_chord_latch')));
            var st = status(host, api, '');
            var i = 0, mistakes = 0, dirty = false, settle = null, done = false;
            function show() {
                var c = chords[i];
                head.textContent = api.tt('p_chord_n', { n: i + 1, total: chords.length }) + ' · ' + c.name;
                staffHost.innerHTML = '';
                renderStaff(staffHost, api, { clef: c.midis[0] < 57 && step.clef !== 'treble' ? 'bass' : (step.clef || 'treble'), key: step.key || 0, time: null, fixed: true, events: [{ t: 0, d: 4, n: c.notes }] });
                kb.unmark('is-target');
                kb.unmark('is-ok');
                if (step.show !== false) c.midis.forEach(function (m) { kb.mark(m, 'is-target'); });
                kb.relabel(km);
                dirty = false;
                st.set(api.tt('p_chord_play', { name: c.name }));
            }
            function accept() {
                var c = chords[i];
                c.midis.forEach(function (m) { kb.mark(m, 'is-ok'); });
                st.set(api.tt('p_chord_ok', { name: c.name }), 'is-good');
                if (dirty) mistakes++;
                done = true;
                setTimeout(function () {
                    if (!api.alive()) return;
                    kb.unlatch();
                    hub.releaseAll();
                    i++;
                    done = false;
                    if (i >= chords.length) {
                        var score = (chords.length - mistakes) / chords.length;
                        api.answer({ correct: score >= (step.pass || 0.75) - 1e-9, score: score, explain: t.explain || null });
                        return;
                    }
                    show();
                }, 700);
            }
            function check() {
                if (done || i >= chords.length) return;
                var c = chords[i];
                var held = hub.held();
                var pcs = held.map(pcOf).filter(function (x, k, a) { return a.indexOf(x) === k; });
                var exact = step.exact === true;
                var ok = exact
                    ? held.length === c.midis.length && c.midis.every(function (m) { return held.indexOf(m) >= 0; })
                    : pcs.length === c.pcs.length && c.pcs.every(function (p) { return pcs.indexOf(p) >= 0; });
                if (ok) { accept(); return; }
                if (settle) clearTimeout(settle);
                if (held.length >= c.midis.length) {
                    settle = setTimeout(function () {
                        if (done || !api.alive()) return;
                        var h2 = hub.held(), p2 = h2.map(pcOf);
                        if (h2.length < c.midis.length) return;
                        var extra = h2.filter(function (m) { return c.pcs.indexOf(pcOf(m)) < 0; }).map(function (m) { return label(m); });
                        var missing = c.midis.filter(function (m) { return p2.indexOf(pcOf(m)) < 0; }).map(function (m) { return label(m); });
                        dirty = true;
                        var parts = [];
                        if (extra.length) parts.push(api.tt('p_chord_extra', { notes: extra.join(', ') }));
                        if (missing.length) parts.push(api.tt('p_chord_missing', { notes: missing.join(', ') }));
                        st.set(parts.join(' ') || api.tt('p_chord_octave'), 'is-bad');
                    }, 450);
                }
            }
            hub.listen(function () { check(); });
            if (M && MC) {
                M.onListener = function (L) {
                    var okN = 0;
                    chordStream(L, function (r) {
                        if (done || i >= chords.length || !r.pooled) { okN = 0; return; }
                        var c = chords[i];
                        var target = { root: c.info ? c.info.root : c.pcs[0], quality: c.info ? c.info.quality : 'maj', pcs: c.pcs, strings: c.midis };
                        var j = MC.analysis.judgeChord(r.pooled, target, r.bass);
                        okN = j.ok ? okN + 1 : 0;
                        if (okN >= MC.LIMITS.CHORD_HOLD_N + 1) { okN = 0; accept(); }
                    });
                };
            }
            api.secondary(api.tt('p_chord_clear'), function () { kb.unlatch(); hub.releaseAll(); st.set(api.tt('p_chord_play', { name: chords[i] ? chords[i].name : '' })); });
            show();
        }
    });

    /* Ручки стенда: разбор нот и ABC без браузера; последний прогон «в темпе»;
       MIDI-сообщение тем же путём, что от MidiInput (Web MIDI в Playwright
       не подделать). */
    var lastRun = null;
    function midiIn(m) {
        var t = perfNow();
        if (m.type === 'on') midiHubs.forEach(function (h) { h.on(m.note, 'midi', t, (m.vel || 90) / 127); });
        else if (m.type === 'off') midiHubs.forEach(function (h) { h.off(m.note, 'midi', t); });
    }
    window.__pianoCourse = {
        parse: parse, label: label, toAbc: toAbc, fromMidi: fromMidi, liftQuiet: liftQuiet,
        MIC_DELAY_S: function () { return MIC_DELAY_S; },
        lastRun: function () { return lastRun; },
        midiIn: midiIn
    };

    function boot() { var s = midiSession(); if (s) s.reconnect(); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else setTimeout(boot, 0);
})();
